import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormatterService } from '../formatter/formatter.service';
import { AlertService, LoadingService } from 'ngx-kuv-tools';
import { DateService } from '../date/date.service';
import { ReunionService } from 'src/app/components/reunion/reunion.service';
import { HelperService } from '../helper/helper.service';
import { SentryService } from '../sentry/sentry.service';
import { MotivoReunionService } from 'src/app/components/motivo-reunion/motivo-reunion.service';
import { SesionService } from '../sesion/sesion.service';
import { SucursalService } from 'src/app/components/sucursal/sucursal.service';
import { EmpresaService } from 'src/app/components/empresa/empresa.service';

@Component({
  selector: 'app-agendar-iframe',
  templateUrl: './agendar-iframe.component.html',
  styleUrls: ['./agendar-iframe.component.scss']
})
export class AgendarIframeComponent implements OnInit, OnDestroy {
  completado: boolean = false;
  sitioNoDisponible: boolean = false;
  ocultarTiempoRestante: boolean = true;

  totalPasos: number = 6;
  pasoActual: number = 0;

  nombreEmpresa: string = '';
  empresaPublicToken: string = '';
  duracionReunion: number = 30;

  // Temporizador de sesión
  tiempoRestante: number = 0; // Segundos restantes
  intervalTimer: any;
  sesionExpirada: boolean = false;

  sucursales: any[] = [];

  motivos: any[] = [];

  sucursalSeleccionada: string = '';
  sucursalSeleccionadaNombre: string = '';

  motivoSeleccionado: string = '';
  motivoSeleccionadoNombre: string = '';

  nombreCliente: string = '';

  apellidoCliente: string = '';

  emailCliente: string = '';

  patenteVehiculo: string = '';

  fechaSeleccionada: Date | string | null = null;

  horaSeleccionada: string | null = null;

  horasDisponibles: string[] = [];
  horasBloqueadas: string[] = [];

  // Fecha mínima permitida (hoy)
  get fechaMinima(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  constructor(
    public formatterService: FormatterService,
    private alerts: AlertService,
    private loading: LoadingService,
    private dateService: DateService,
    private service: ReunionService,
    private helperService: HelperService,
    private sentryService: SentryService,
    private motivoReunionService: MotivoReunionService,
    private sesionService: SesionService,
    private sucursalService: SucursalService,
    private empresaService: EmpresaService,
  ) { }

  ngOnInit(): void {
    this.generarHorasDisponibles();
    this.iniciarTemporizador();
    this.obtenerInfoToken();
  }

  ngOnDestroy(): void {
    this.detenerTemporizador();
  }

  obtenerInfoToken(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const payloadParam = urlParams.get('payload');
    if (!payloadParam) {
      this.sitioNoDisponible = true;
      return;
    }
    try {
      const decoded = atob(payloadParam);
      const decodedAndCleaned = decoded.trim().replace(/^'|'$/g, "");
      const payload = JSON.parse(decodedAndCleaned);
      this.empresaPublicToken = payload.token_publico;
    } catch (error) {
      console.error(error);
      this.sitioNoDisponible = true;
    }
  }

  obtenerInfoEmpresa(token: string): void {
    this.loading.show();
    this.empresaService.getInfoEmpresaDesdeTokenParaAnonimo(token).subscribe({
      next: (res) => {
        this.loading.hide();
        this.nombreEmpresa = res.empresa.nombre || '';
        this.duracionReunion = res.duracion || 30;
        this.ocultarTiempoRestante = false;
      },
      error: (err) => {
        console.error(err);
        this.loading.hide();
        this.alerts.addAlert("No se pudo obtener la información de la empresa.", "danger");
        this.helperService.showErrorAlerts(err);
        this.sitioNoDisponible = true;
      }
    });
  }

  iniciarTemporizador(): void {
    // Verificar token cada segundo
    this.intervalTimer = setInterval(() => {
      if (!this.sesionService.tokenAnonimoValido()) {
        this.sesionExpirada = true;
        this.detenerTemporizador();
        // Solo mostrar alerta si no está en el paso inicial
        if (this.pasoActual > 0) {
          this.alerts.addAlert('Tu sesión ha expirado. Por favor, inicia el proceso nuevamente.', 'warning');
        }
        setTimeout(() => {
          this.resetearFormulario();
          this.pasoActual = 0;
        }, 2000);
      } else {
        // Calcular tiempo restante basado en el token JWT
        try {
          const token = this.sesionService.tokenAnonimo;
          if (token && token !== '') {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Math.floor(new Date().getTime() / 1000);
            this.tiempoRestante = Math.max(0, payload.exp - now);
          }
        } catch (error) {
          console.error('Error al calcular tiempo restante:', error);
        }
      }
    }, 1000);
  }

  detenerTemporizador(): void {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }
  }

  get tiempoFormateado(): string {
    const minutos = Math.floor(this.tiempoRestante / 60);
    const segundos = this.tiempoRestante % 60;
    return `${minutos}:${segundos.toString().padStart(2, '0')}`;
  }

  resetearFormulario(): void {
    this.sucursalSeleccionada = '';
    this.motivoSeleccionado = '';
    this.nombreCliente = '';
    this.apellidoCliente = '';
    this.emailCliente = '';
    this.patenteVehiculo = '';
    this.fechaSeleccionada = null;
    this.horaSeleccionada = null;
    this.horasDisponibles = [];
    this.horasBloqueadas = [];
    this.sucursales = [];
    this.motivos = [];
    this.sesionExpirada = false;
    localStorage.removeItem('token-anonimo');
  }

  get isLastStep(): boolean {
    return this.pasoActual == this.totalPasos;
  }

  get isFirstStep(): boolean {
    return this.pasoActual == 1;
  }

  get canNextStep(): boolean {
    return this.pasoActual < this.totalPasos;
  }

  get canPrevStep(): boolean {
    return this.pasoActual > 0;
  }

  nextStep(): void {
    if (!this.canNextStep) return;

    // Verificar si la sesión sigue válida antes de continuar
    if (this.pasoActual > 0 && this.sesionExpirada) {
      this.alerts.addAlert('Tu sesión ha expirado. Por favor, recarga la página.', 'danger');
      return;
    }

    switch (this.pasoActual) {
      case 1:
        if (!this.sucursalSeleccionada || this.sucursalSeleccionada == '') {
          this.alerts.addAlert('Debe seleccionar una sucursal para continuar.', 'warning');
          return;
        }
        break;
      case 2:
        if (!this.nombreCliente || this.nombreCliente == '') {
          this.alerts.addAlert('Debe ingresar el nombre del cliente para continuar.', 'warning');
          return;
        }
        if (!this.apellidoCliente || this.apellidoCliente == '') {
          this.alerts.addAlert('Debe ingresar el apellido del cliente para continuar.', 'warning');
          return;
        }
        if (!this.emailCliente || this.emailCliente == '') {
          this.alerts.addAlert('Debe ingresar el correo electrónico del cliente para continuar.', 'warning');
          return;
        }
        if (!this.patenteVehiculo || this.patenteVehiculo == '') {
          this.alerts.addAlert('Debe ingresar la patente del vehículo para continuar.', 'warning');
          return;
        }
        break;
      case 3:
        if (!this.motivoSeleccionado || this.motivoSeleccionado == '') {
          this.alerts.addAlert('Debe seleccionar un motivo para continuar.', 'warning');
          return;
        }
        break;
      case 4:
        if (!this.fechaSeleccionada || !this.horaSeleccionada) {
          this.alerts.addAlert('Debe seleccionar fecha y hora para continuar.', 'warning');
          return;
        }
        break;
    }

    if (this.pasoActual == 0) {
      if (this.sesionService.tokenAnonimoValido()) {
        this.getSucursalesAnonimo();
      } else {
        this.requestAnonToken(true);
        return;
      }
    } else if (this.pasoActual == 2 && this.motivos.length == 0) {
      this.getMotivosReunionesAnonimo();
    }

    if(this.pasoActual == 5) {
      this.createReunionAnonimo();
      return;
    }
    this.pasoActual++;
  }

  prevStep(): void {
    if (this.canPrevStep) {
      this.pasoActual--;
    }
  }

  onChangeSucursal(): void {
    const sucursal = this.sucursales.find(s => s.id == this.sucursalSeleccionada);
    if (sucursal) {
      this.sucursalSeleccionadaNombre = sucursal.nombre;
    }
  }

  onChangeMotivo(): void {
    const motivo = this.motivos.find(m => m.id == this.motivoSeleccionado);
    if (motivo) {
      this.motivoSeleccionadoNombre = motivo.nombre;
    }
  }

  requestAnonToken(goToNext: boolean = false): void {
    this.loading.show();
    this.sesionService.loginAnonimo().subscribe({
      next: (res) => {
        this.loading.hide();
        if (!res?.token) {
          this.alerts.addAlert("Error al solicitar la información.", "danger");
          return;
        }
        localStorage.setItem('token-anonimo', res.token);
        this.iniciarTemporizador();
        if (goToNext) {
          if (this.sucursales.length == 0) {
            this.getSucursalesAnonimo();
          }
          this.pasoActual++;
        }
      },
      error: (err) => {
        this.loading.hide();
        console.error(err);
        this.sentryService.captureException(err);
        this.alerts.addAlert("Error al solicitar la información.", "danger");
        this.helperService.showErrorAlerts(err);
      }
    });
  }

  getSucursalesAnonimo(): void {
    this.loading.show();
    this.sucursalService.selectAnonimo(this.empresaPublicToken).subscribe({
      next: (res) => {
        this.loading.hide();
        this.obtenerInfoEmpresa(this.empresaPublicToken);
        this.sucursales = res;
        if (this.sucursales.length == 1) {
          this.sucursalSeleccionada = this.sucursales[0].id;
          this.sucursalSeleccionadaNombre = this.sucursales[0].nombre;
        }
      },
      error: (err) => {
        console.error(err);
        this.pasoActual = 0;
        this.loading.hide();
        this.sentryService.captureException(err);
        this.alerts.addAlert("No se pudo obtener la lista de sucursales.", "danger");
        this.helperService.showErrorAlerts(err);
      }
    });
  }

  getMotivosReunionesAnonimo(): void {
    this.loading.show();
    this.motivoReunionService.selectAnonimo(parseInt(this.sucursalSeleccionada)).subscribe({
      next: (res) => {
        this.loading.hide();
        this.motivos = res;
        if (this.motivos.length == 1) {
          this.motivoSeleccionado = this.motivos[0].id;
          this.motivoSeleccionadoNombre = this.motivos[0].nombre;
        }
      },
      error: (err) => {
        console.error(err);
        this.loading.hide();
        this.sentryService.captureException(err);
        this.alerts.addAlert("No se pudo obtener la lista de motivos de reuniones.", "danger");
        this.helperService.showErrorAlerts(err);
      }
    });
  }

  createReunionAnonimo(): void {
    if(this.sesionExpirada) {
      this.alerts.addAlert('Tu sesión ha expirado. Por favor, recarga la página.', 'danger');
      return;
    }
    if (!this.fechaSeleccionada || !this.horaSeleccionada) {
      this.alerts.addAlert('Debe seleccionar fecha y hora para continuar.', 'warning');
      return;
    }

    // Validar que la fecha y hora seleccionadas no hayan pasado
    let fechaSeleccionadaDate: Date;
    if (typeof this.fechaSeleccionada === 'string') {
      const [year, month, day] = this.fechaSeleccionada.split('-').map(Number);
      fechaSeleccionadaDate = new Date(year, month - 1, day);
    } else {
      fechaSeleccionadaDate = new Date(this.fechaSeleccionada);
    }

    const [horaSeleccionada, minutoSeleccionado] = this.horaSeleccionada.split(':').map(Number);
    const fechaHoraSeleccionada = new Date(
      fechaSeleccionadaDate.getFullYear(),
      fechaSeleccionadaDate.getMonth(),
      fechaSeleccionadaDate.getDate(),
      horaSeleccionada,
      minutoSeleccionado,
      0,
      0
    );

    const ahora = new Date();

    if (fechaHoraSeleccionada <= ahora) {
      this.alerts.addAlert('La fecha y hora seleccionadas ya han pasado. Por favor, seleccione una hora futura.', 'warning');
      this.pasoActual = 4; // Regresar al paso de selección de fecha/hora
      // Recargar los bloques disponibles
      const start = new Date(fechaSeleccionadaDate.getFullYear(), fechaSeleccionadaDate.getMonth(), fechaSeleccionadaDate.getDate(), 0, 0, 0, 0);
      const end = new Date(fechaSeleccionadaDate.getFullYear(), fechaSeleccionadaDate.getMonth(), fechaSeleccionadaDate.getDate(), 23, 59, 59, 999);
      this.getReuniones(this.dateService.formatDateISO8601(start), this.dateService.formatDateISO8601(end));
      return;
    }

    const fechaInicioStr = this.dateService.formatDateISO8601WithTimeAsString(
      fechaSeleccionadaDate,
      this.horaSeleccionada || '00:00'
    );

    // Calcular fecha término sumando la duración de la reunión
    const fechaInicioDate = new Date(fechaInicioStr);
    const fechaTerminoDate = new Date(fechaInicioDate.getTime() + this.duracionReunion * 60000); // minutos en ms
    const fechaTerminoStr = this.dateService.formatDateISO8601WithTimeAsString(
      fechaTerminoDate,
      this.horaSeleccionada || '00:00'
    );

    const data = {
      public_token: this.empresaPublicToken,
      sucursal_id: this.sucursalSeleccionada,
      motivo_reunion_id: this.motivoSeleccionado,
      nombre_cliente: this.nombreCliente,
      apellido_cliente: this.apellidoCliente,
      email_cliente: this.emailCliente,
      patente_vehiculo: this.patenteVehiculo,
      fecha_inicio: fechaInicioStr,
      fecha_termino: fechaTerminoStr,
      tipo_registro: 3 // Iframe Web
    };

    this.loading.show();
    this.service.createReunionAnonimo(data).subscribe({
      next: (res) => {
        this.loading.hide();
        this.pasoActual++;
        this.completado = true;
      },
      error: (err) => {
        if (err.status === 403) {
          this.pasoActual = 4;
          this.alerts.addAlert('La hora seleccionada ya no está disponible. Por favor, elija otra hora.', 'warning');
          this.getReuniones(
            this.dateService.formatDateISO8601WithTimeAsString(new Date(this.fechaSeleccionada || ''), '00:00'),
            this.dateService.formatDateISO8601WithTimeAsString(new Date(this.fechaSeleccionada || ''), '23:59')
          );
          return;
        }
        console.error(err);
        this.loading.hide();
        this.sentryService.captureException(err);
        this.alerts.addAlert("No se pudo crear la reunión.", "danger");
        this.helperService.showErrorAlerts(err);
      }
    });
  }

  getReuniones(fechaInicio: string, fechaTermino: string): void {
    this.loading.show();
    this.service.selectRangoParaAnonimo(this.sucursalSeleccionada, fechaInicio, fechaTermino).subscribe({
      next: (res) => {
        this.loading.hide();
        // Actualizar bloques disponibles basándose en las reuniones
        this.generarBloquesDisponiblesDesdeReuniones(res);
      },
      error: (err) => {
        console.error(err);
        this.loading.hide();
        this.sentryService.captureException(err);
        this.alerts.addAlert("No se pudo obtener la lista de reuniones.", "danger");
        this.helperService.showErrorAlerts(err);
      }
    });
  }

  fechaCambiada(): void {
    if (!this.fechaSeleccionada) {
      this.horasDisponibles = [];
      this.horasBloqueadas = [];
      return;
    }

    // Convertir a Date asegurándose de usar la zona horaria local
    let fecha: Date;
    if (typeof this.fechaSeleccionada === 'string') {
      // Si es string del input date (YYYY-MM-DD), parsearlo en zona horaria local
      const [year, month, day] = this.fechaSeleccionada.split('-').map(Number);
      fecha = new Date(year, month - 1, day);
    } else {
      fecha = new Date(this.fechaSeleccionada);
    }

    if (isNaN(fecha.getTime())) {
      this.alerts.addAlert('Fecha inválida', 'warning');
      return;
    }

    // Crear fecha de inicio en el mismo día a las 00:00:00
    const start = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 0, 0, 0, 0);

    // Crear fecha de fin en el mismo día a las 23:59:59
    const end = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 23, 59, 59, 999);

    this.getReuniones(this.dateService.formatDateISO8601(start), this.dateService.formatDateISO8601(end));
  }

  // Generar todos los bloques de tiempo posibles de 0:00 a 24:00 cada X minutos
  generarHorasDisponibles(): void {
    this.horasDisponibles = [];
    const horaInicio = 0; // 0:00 AM
    const horaFin = 24; // 12:00 AM

    for (let hora = horaInicio; hora < horaFin; hora++) {
      for (let minuto = 0; minuto < 60; minuto += this.duracionReunion) {
        const horaStr = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
        this.horasDisponibles.push(horaStr);
      }
    }
  }

  // Generar bloques de tiempo disponibles basándose en las reuniones existentes
  generarBloquesDisponiblesDesdeReuniones(reuniones: any[]): void {
    if (!this.fechaSeleccionada) {
      this.horasDisponibles = [];
      this.horasBloqueadas = [];
      return;
    }

    // Generar todos los bloques posibles primero
    this.generarHorasDisponibles();

    // Convertir a Date asegurándose de usar la zona horaria local (igual que en fechaCambiada)
    let fechaDate: Date;
    if (typeof this.fechaSeleccionada === 'string') {
      // Si es string del input date (YYYY-MM-DD), parsearlo en zona horaria local
      const [year, month, day] = this.fechaSeleccionada.split('-').map(Number);
      fechaDate = new Date(year, month - 1, day);
    } else {
      fechaDate = new Date(this.fechaSeleccionada);
    }

    // Obtener todas las horas ocupadas del día seleccionado
    const fechaSeleccionadaStr = this.dateService.formatDateISO8601(fechaDate);
    const horasOcupadas: string[] = [];

    reuniones.forEach(reunion => {
      const fechaInicio = new Date(reunion.fecha_inicio);
      let fechaTermino = new Date(reunion.fecha_termino);
      
      // Si fecha_inicio y fecha_termino son iguales, agregar la duración de la reunión
      if (fechaInicio.getTime() === fechaTermino.getTime()) {
        fechaTermino = new Date(fechaInicio.getTime() + this.duracionReunion * 60000);
      }
      
      const fechaReunionStr = this.dateService.formatDateISO8601(fechaInicio);

      // Solo procesar reuniones del mismo día
      if (fechaReunionStr === fechaSeleccionadaStr) {
        // Calcular todos los bloques de X minutos ocupados por esta reunión
        let currentTime = new Date(fechaInicio);
        const endTime = fechaTermino;

        // Marcar el bloque de inicio como ocupado siempre
        const horaInicio = currentTime.getHours();
        const minutoInicio = currentTime.getMinutes();
        const horaInicioStr = `${horaInicio.toString().padStart(2, '0')}:${minutoInicio.toString().padStart(2, '0')}`;
        
        if (!horasOcupadas.includes(horaInicioStr)) {
          horasOcupadas.push(horaInicioStr);
        }

        // Avanzar y marcar bloques adicionales si la reunión dura más
        currentTime.setMinutes(currentTime.getMinutes() + this.duracionReunion);
        
        while (currentTime < endTime) {
          const hora = currentTime.getHours();
          const minuto = currentTime.getMinutes();
          const horaStr = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;

          if (!horasOcupadas.includes(horaStr)) {
            horasOcupadas.push(horaStr);
          }

          // Avanzar X minutos
          currentTime.setMinutes(currentTime.getMinutes() + this.duracionReunion);
        }
      }
    });

    // Bloquear horas pasadas si es el día de hoy
    const hoy = new Date();
    const esHoy = fechaDate.getFullYear() === hoy.getFullYear() &&
                  fechaDate.getMonth() === hoy.getMonth() &&
                  fechaDate.getDate() === hoy.getDate();

    if (esHoy) {
      const horaActual = hoy.getHours();
      const minutoActual = hoy.getMinutes();

      this.horasDisponibles.forEach(hora => {
        const [h, m] = hora.split(':').map(Number);
        
        // Si la hora es anterior a la actual, o es la misma hora pero los minutos ya pasaron
        if (h < horaActual || (h === horaActual && m <= minutoActual)) {
          if (!horasOcupadas.includes(hora)) {
            horasOcupadas.push(hora);
          }
        }
      });
    }

    // Actualizar las horas bloqueadas
    this.horasBloqueadas = horasOcupadas;
  }

  // Seleccionar una hora específica
  seleccionarHora(hora: string): void {
    if (this.isHoraBloqueada(hora)) {
      this.alerts.addAlert('Esta hora no está disponible.', 'warning');
      return;
    }
    this.horaSeleccionada = hora;
  }

  // Verificar si una hora está bloqueada
  isHoraBloqueada(hora: string): boolean {
    return this.horasBloqueadas.includes(hora);
  }

  // Verificar si una hora está seleccionada
  isHoraSeleccionada(hora: string): boolean {
    return this.horaSeleccionada === hora;
  }

  // Obtener solo las horas que están disponibles (no bloqueadas)
  get horasSoloDisponibles(): string[] {
    return this.horasDisponibles.filter(hora => !this.isHoraBloqueada(hora));
  }

  // Obtener cantidad de horas disponibles
  get cantidadHorasDisponibles(): number {
    return this.horasSoloDisponibles.length;
  }
}
